import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Plus, Trash, MagicWand } from "@phosphor-icons/react";
import { Section, Pill, EmptyState, severityTone } from "./_shared";
import { AIEnrichButton, AIBulkGenerateModal, AIAssistant } from "./_ai";

const STATUS_TONE = { open: "red", in_progress: "orange", resolved: "teal" };

export default function IssueLens({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", status: "open", owner: "" });
  const [creating, setCreating] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/issues`);
      setItems(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/issues`, form);
      setItems((p) => [data, ...p]);
      setForm({ title: "", description: "", status: "open", owner: "" });
    } catch { toast.error("Failed to add"); }
    finally { setCreating(false); }
  };

  const analyze = async (id) => {
    setAnalyzingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/issues/${id}/analyze`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Root cause detected");
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzingId(null); }
  };

  const resolution = async (id) => {
    setResolvingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/issues/${id}/resolution`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Resolution plan ready");
    } catch { toast.error("Failed"); }
    finally { setResolvingId(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/projects/${projectId}/issues/${id}`); setItems(p => p.filter(x => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="issue-lens">
      <Section
        eyebrow="Log"
        title="New issue"
        action={
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold hover:bg-[#0020F5]"
            data-testid="bulk-gen-issue-button"
          >
            <MagicWand size={14} weight="fill" /> AI bulk generate
          </button>
        }
      >
        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto_auto_auto_auto] gap-3">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Issue title"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="issue-title-input" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What's happening?"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="issue-desc-input" />
          <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}
            placeholder="Owner"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 w-32"
            data-testid="issue-owner-input" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="h-12 px-4 border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="issue-status-select">
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
          <button type="submit" disabled={creating}
            className="h-12 px-6 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="add-issue-button">
            <Plus size={16} weight="bold" /> {creating ? "Adding..." : "Add"}
          </button>
          <AIEnrichButton
            projectId={projectId}
            lens="issue"
            partial={{ title: form.title, description: form.description }}
            onEnriched={(enriched) => setForm((p) => ({
              ...p,
              title: enriched.title || p.title,
              description: enriched.description || p.description,
              status: enriched.status || p.status,
            }))}
          />
        </form>
      </Section>

      <Section eyebrow="Root cause · ML 3.2" title={`Issues (${items.length})`}>
        {loading ? <div className="text-zinc-500 text-sm">Loading…</div> :
          items.length === 0 ? <EmptyState title="No issues logged" hint="Log issues to have AI surface root causes and resolution plans." testid="empty-issues" /> :
          <div className="divide-y divide-zinc-200 border border-zinc-200">
            {items.map(i => (
              <div key={i.id} className="p-5" data-testid={`issue-item-${i.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display font-bold text-lg">{i.title}</h3>
                      <Pill tone={STATUS_TONE[i.status]}>{i.status.replace("_", " ")}</Pill>
                      {i.urgency && <Pill tone={severityTone(i.urgency)}>{i.urgency} urgency</Pill>}
                      {i.impact_level && <Pill tone={i.impact_level === "H" ? "red" : i.impact_level === "M" ? "orange" : "green"}>Impact {i.impact_level}</Pill>}
                      {i.owner && <Pill>Owner: {i.owner}</Pill>}
                    </div>
                    {i.description && <p className="text-sm text-zinc-700 mt-2">{i.description}</p>}

                    {i.root_cause && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-4 border border-zinc-200 p-4 bg-zinc-50">
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Root cause</div>
                          <p className="text-sm text-zinc-900 mt-1">{i.root_cause}</p>
                        </div>
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Contributing factors</div>
                          <ul className="mt-1 text-sm text-zinc-700 space-y-1 list-disc list-inside">
                            {(i.contributing_factors || []).map((f, k) => <li key={`cf-${k}-${(f || "").slice(0, 30)}`}>{f}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}

                    {(i.proposed_solution || i.preventive_measure) && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-4 border-l-4 border-[#00C7BE] pl-4">
                        {i.proposed_solution && (
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00C7BE]">Proposed solution</div>
                            <p className="text-sm text-zinc-800 mt-1">{i.proposed_solution}</p>
                          </div>
                        )}
                        {i.preventive_measure && (
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00C7BE]">Preventive measure</div>
                            <p className="text-sm text-zinc-800 mt-1">{i.preventive_measure}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-44">
                    <button onClick={() => analyze(i.id)} disabled={analyzingId === i.id}
                      className="inline-flex items-center gap-2 h-9 px-3 bg-[#FF9500] text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50"
                      data-testid={`analyze-issue-${i.id}`}>
                      <Sparkle size={12} weight="fill" />
                      {analyzingId === i.id ? "Analysing…" : "Root cause"}
                    </button>
                    <button onClick={() => resolution(i.id)} disabled={resolvingId === i.id}
                      className="inline-flex items-center gap-2 h-9 px-3 bg-[#0020F5] text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                      data-testid={`resolution-issue-${i.id}`}>
                      <MagicWand size={12} weight="fill" />
                      {resolvingId === i.id ? "Working…" : "Resolution plan"}
                    </button>
                    <button onClick={() => remove(i.id)} className="inline-flex items-center justify-center h-9 px-3 border border-zinc-300 text-zinc-700 hover:text-[#FF3B30] hover:border-[#FF3B30] text-xs" data-testid={`delete-issue-${i.id}`}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      </Section>
      <AIBulkGenerateModal
        projectId={projectId}
        lens="issue"
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => load()}
      />
      <AIAssistant projectId={projectId} lens="issue" label="Issue" />
    </div>
  );
}
